/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  DedicatedEndpointStatusWithPhaseDocument,
  type DedicatedEndpointStatusWithPhaseQuery,
} from '@fai/friendli-suite/generated/client/graphql';

import { shouldEndpointStatusBePolled } from './endpoint-status';

/**
 * Replacement of DedicatedEndpointPhase from the generated client.
 *
 * since DedicatedEndpointPhaseInitializing, DedicatedEndpointPhaseAwaking uses path alias
 * DedicatedEndpointPhase cannot be inferred properly.
 */
export type QueryBasedEndpointStatusWithPhase = NonNullable<
  DedicatedEndpointStatusWithPhaseQuery['dedicatedEndpoint']
>['phase'];

export const useEndpointStatusWithPhase = (endpointId?: string) => {
  const { endpointId: endpointIdParam } = useParams();

  const {
    data: _data,
    loading,
    startPolling,
    stopPolling,
  } = useQuery(DedicatedEndpointStatusWithPhaseDocument, {
    variables: { endpointId: endpointId ?? endpointIdParam ?? '' },
    fetchPolicy: 'cache-and-network',
  });

  const status = _data?.dedicatedEndpoint?.status;
  const phase = _data?.dedicatedEndpoint?.phase;

  useEffect(() => {
    if (shouldEndpointStatusBePolled(status)) {
      startPolling(1000);
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [status, startPolling, stopPolling]);

  return { status, phase, loading };
};
